import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Instagram, Facebook } from "lucide-react";
import { cn } from "../lib/utils";
import { motion, AnimatePresence } from "framer-motion";

const navLinks = [
  { name: "Forside", href: "/" },
  { name: "Spørg Ikke", href: "/spoerg-ikke" },
  { name: "Foredrag", href: "/foredrag" },
  { name: "Jeg tilbyder", href: "/jeg-tilbyder" },
  { name: "Om", href: "/om" },
  { name: "Kontakt", href: "/kontakt" },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-700 border-b",
        (scrolled || isOpen)
          ? "bg-white border-slate-100 py-4"
          : "bg-transparent border-transparent py-8"
      )}
    >
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between">
          {/* Logo container */}
          <div className="flex items-center justify-start xl:flex-1">
            <Link
              to="/"
              className="flex items-center gap-4 group"
            >
              <div className={cn(
                "w-auto transition-all duration-700 ease-in-out",
                location.pathname === "/" && !scrolled
                  ? "h-24 md:h-32 xl:h-44"
                  : "h-16 xl:h-24"
              )}>
                <img 
                  src="https://assets.auxodev.dk/assets/d91f10e5-8d69-4d90-b5bd-7f70fc130edc/28106c49-a2b8-4b7b-bd26-274e9d86034f.png" 
                  alt="Logo" 
                  className="h-full w-auto object-contain"
                />
              </div>
            </Link>
          </div>

          {/* Desktop Nav - Centered */}
          <div className="hidden xl:flex flex-1 justify-center">
            <div className="flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    "text-sm font-semibold tracking-[0.1em] uppercase transition-all duration-300 relative py-1 whitespace-nowrap",
                    location.pathname === link.href
                      ? "text-red-600"
                      : "text-slate-600 hover:text-slate-900"
                  )}
                >
                  {link.name}
                  {location.pathname === link.href && (
                    <motion.div
                      layoutId="nav-underline"
                      className="absolute bottom-0 left-0 right-0 h-px bg-red-600"
                    />
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Right Action - Book nu */}
          <div className="hidden xl:flex flex-1 justify-end">
            <Link
              to="/kontakt"
              className="group relative inline-flex items-center justify-center px-6 py-2.5 border border-slate-900 text-slate-900 text-xs font-bold tracking-[0.2em] uppercase transition-all hover:bg-red-600 hover:text-white hover:border-red-600"
            >
              Book nu
            </Link>
          </div>

          {/* Mobile Toggle */}
          {!isOpen && (
            <button
              className="xl:hidden p-2 text-slate-900 ml-auto"
              onClick={() => setIsOpen(true)}
            >
              <Menu size={24} />
            </button>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 200 }}
            className="fixed inset-0 z-40 bg-white xl:hidden pt-32 px-8 h-screen overflow-y-auto"
          >
            {/* Close Button inside Overlay */}
            <button
              className="absolute top-8 right-6 p-2 text-slate-900"
              onClick={() => setIsOpen(false)}
            >
              <X size={32} />
            </button>

            <div className="flex flex-col gap-8">
              {navLinks.map((link, idx) => (
                <motion.div
                  key={link.href}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Link
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      "text-4xl font-serif italic transition-colors block",
                      location.pathname === link.href
                        ? "text-red-600"
                        : "text-slate-900"
                    )}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mt-12 mb-12"
              >
                <Link
                  to="/kontakt"
                  onClick={() => setIsOpen(false)}
                  className="inline-block w-full text-center py-5 border border-slate-900 text-slate-900 font-bold tracking-widest uppercase hover:bg-red-600 hover:text-white hover:border-red-600 transition-all duration-300"
                >
                  Book Foredrag
                </Link>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="mt-auto pb-12 flex justify-center gap-8"
              >
                <a 
                  href="https://www.instagram.com/candragaarde/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-red-600 transition-colors"
                >
                  <Instagram size={24} />
                </a>
                <a 
                  href="https://www.facebook.com/TaJaHattenPaa/?utm_source=ig&utm_medium=social&utm_content=link_in_bio" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-400 hover:text-red-600 transition-colors"
                >
                  <Facebook size={24} />
                </a>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
